<template lang="pug">
#room-schedule-setting-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 空室／空床情報の公開設定
      .buttons
        button.btn(@click="onExampleModal" type="button") 設定例

    .box-body.register-body
      .register-row
        .register-group
          .register-key
            span.key-name 公開状態
            span.validation-label 必須
          .register-value
            RoomSchedulePublicSwitch(v-model="isOpened")
      FormInputRadio(v-model="publicType" keyName="公開情報" name="publicType" :options="publicTypeOptions"
        :validationRules="{ required: true }" required)

      .only-schedule-setting(v-if="publicType == 'schedule'")
        FormInputRadio(v-model="availableDate" keyName="公開開始日" name="availableDate" :options="availableDateOptions"
          :validationRules="{ required: true }" required)
        FormDatePicker(v-model="endDate" :date.sync="endDateDate" key-name="公開最終日" name="endDate" placeholder="公開最終日")

      .only-custom-setting(v-if="publicType == 'custom'")
        .register-row
          .register-group
            .register-key
              span.key-name 公開情報の設定
            span 公開する部屋種別とそれぞれの空床情報（空床数など）、使用有無を設定できます。
            CustomPublicSettingTable(v-model="customPublicSettings" :isLoading="isBusy")

      FormTextarea(v-model="message" key-name="連絡事項" name="message"
        placeholder="例：利用希望がございましたら、施設までご連絡ください。TELと表示されている日はお電話でご確認ください。"
        :validation-rules="{ required: false, max: 100 }" :maxlength="100" :styles="{ maxWidth: '720px', width: '720px', minHeight: '160px' }")
      //- TODO #3957
      //- FormInputRadio(v-model="useProvisionalApplication" keyName="仮申込み" name="useProvisionalApplication" :options="useProvisionalApplicationOptions"
        :validationRules="{ required: true }" required)

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="goBack" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="空室／空床情報の公開設定を更新しますか？" @ok="updateRoomScheduleSetting")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="goBack")
  RoomScheduleSettingExampleModal(ref="roomScheduleSettingExampleModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import RoomScheduleSettingsService, { CustomPublicSetting, PublicType, RoomScheduleSettingGetModel, RoomScheduleSettingPatchModel } from "@/commons/services/RoomScheduleSettings";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import RoomSchedulePublicSwitch from "@/pc/basics/RoomSchedulePublicSwitch.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import FormDatePicker from "@/pc/components/forms/FormDatePicker.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import RoomScheduleSettingExampleModal from "@/pc/components/modals/RoomScheduleSettingExampleModal.vue";
import CustomPublicSettingTable from "@/pc/containers/roomSchedules/CustomPublicSettingTable.vue";

/**
 * ### 空室／空床情報の設定編集画面コンポーネント
 */
@Component({
  $_veeValidate: { validator: "new" },
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    RoomSchedulePublicSwitch,
    FormInputRadio,
    FormTextarea,
    FormDatePicker,
    RoomScheduleSettingExampleModal,
    CustomPublicSettingTable
  }
})
export default class RoomScheduleSetting extends Vue {
  /** 空室／空床情報の設定サービス */
  @Inject("RoomScheduleSettingsService") roomScheduleSettingsService!: RoomScheduleSettingsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 公開状態 */
  isOpened: boolean = false;
  /** 公開情報 */
  publicType: PublicType | null = null;
  /** 公開情報の選択項目 */
  publicTypeOptions: Array<{ value: number | string; label: string }> = [
    { value: PublicType.SCHEDULE, label: "空床スケジュールを公開する" },
    { value: PublicType.CUSTOM, label: "公開情報を自分で設定する" }
  ];
  /** 連絡事項 */
  message: string | null = null;
  /** 仮申込み */
  useProvisionalApplication: boolean = false;
  /** 仮申込みの選択項目 */
  useProvisionalApplicationOptions: Array<{ value: boolean; label: string }> = [
    { value: false, label: "使用しない" },
    { value: true, label: "使用する" }
  ];
  /** 更新日時 */
  updated: number | null = null;

  // 公開情報「空床スケジュールを公開する」の場合に入力
  /** 公開開始日 */
  availableDate: number | null = null;
  /** 公開開始日の選択項目 */
  availableDateOptions: Array<{ value: number | string; label: string }> = [];
  /** 公開終了日 */
  endDate: number | null = null;
  /** 公開終了日(日) */
  endDateDate: string | null = null;

  // 公開情報「公開情報を自分で設定する」の場合に入力
  /** 公開情報（自分で設定） */
  customPublicSettings: Array<CustomPublicSetting> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 空室／空床情報の設定情報を取得する
   * - 取得した設定情報を画面入力情報に格納する
   * - 公開終了日が存在しない場合は今日の日付を使用する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const roomScheduleSetting: RoomScheduleSettingGetModel = await this.getRoomScheduleSetting();

    // 共通項目の格納
    this.isOpened = roomScheduleSetting.isOpened;
    this.publicType = roomScheduleSetting.publicType;
    this.message = roomScheduleSetting.message != null ? roomScheduleSetting.message : "";
    this.useProvisionalApplication = roomScheduleSetting.useProvisionalApplication;
    this.updated = roomScheduleSetting.updated;

    // 空床スケジュールを公開する固有の格納
    this.availableDate = roomScheduleSetting.availableDate != null ? roomScheduleSetting.availableDate : null;
    const availableDateOptions: Array<{ value: number | string; label: string }> = this.createAvailableDateOptions();
    this.availableDateOptions = availableDateOptions;
    this.endDate = roomScheduleSetting.endDate;
    if (roomScheduleSetting.endDate != null) {
      this.endDateDate = this.$dateUtil.convertNumberToString(roomScheduleSetting.endDate, "yyyy-mm-dd");
    } else {
      this.endDateDate = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    }

    // 公開情報を自分で設定するの場合に入力
    this.customPublicSettings = roomScheduleSetting.customPublicSettings;

    this.isBusy = false;

    return;
  }

  /**
   * ### 空室／空床情報の設定取得
   */
  public async getRoomScheduleSetting(): Promise<RoomScheduleSettingGetModel> {
    const response: RoomScheduleSettingGetModel = await this.roomScheduleSettingsService.get(userStore);
    return response;
  }

  /**
   * ### 公開開始日の選択肢を作成する
   */
  public createAvailableDateOptions(): Array<{ value: number | string; label: string }> {
    const availableDateOptions: Array<{ value: number | string; label: string }> = [
      { value: 0, label: "当日から" },
      { value: 1, label: "翌日から" },
      { value: 3, label: "3日後から" },
      { value: 5, label: "5日後から" },
      { value: 7, label: "1週間後から" }
    ];
    return availableDateOptions;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 空室／空床情報の設定を更新する
   *
   * - リクエストボディを作成する
   * - 空室／空床情報の設定を更新する
   * - ステータスコードが409の場合、エラーメッセージを表示して画面を作成しなおす
   */
  public async updateRoomScheduleSetting(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: RoomScheduleSettingPatchModel = this.createRequestBody();
      await this.roomScheduleSettingsService.patch(userStore, requestBody);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### リクエストボディを作成する
   */
  public createRequestBody(): RoomScheduleSettingPatchModel {
    const requestBody: RoomScheduleSettingPatchModel = {
      isOpened: this.isOpened,
      publicType: this.publicType as PublicType,
      availableDate: this.availableDate != null ? this.availableDate : null,
      endDate: this.endDate != null ? this.endDate : null,
      customPublicSettings: this.customPublicSettings,
      message: this.message as string,
      useProvisionalApplication: this.useProvisionalApplication,
      updated: this.updated as number
    };

    return requestBody;
  }

  /**
   * ### 空室スケジュール一覧画面に遷移する
   */
  public goBack(): void {
    const type: number = Number(this.$route.params.type);
    if (type == 0) {
      this.$router.push(PcUrl.RoomScheduleList);
    } else if (type == 1) {
      this.$router.push(PcUrl.OtherOfficeRoomSchedules);
    }
    return;
  }

  /**
   * ### 使用例モーダルを表示する
   */
  public onExampleModal(): void {
    const modal: any = this.$refs.roomScheduleSettingExampleModal;
    if (modal) {
      modal.show();
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
#room-schedule-setting-edit {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
